import { useAuth } from "@/components/auth/auth-context";
import {
  ShareableEntity,
  useMarkEntityRead,
  useMarkEntityUnread,
  useAddInteraction,
  useEntityInteractions,
} from "@/lib/api/shareable-entities";
import { usePostHogEvent } from "@/lib/posthog";
import { type ReactionEmoji } from "./entity-action-bar";
import {
  transformInteractionsToComments,
  transformInteractionsToReactions,
} from "./utils";

type EntityType = "note" | "moment" | "swap" | "hot_takes" | "connect_moment";

interface UseEntityInteractionHandlersOptions {
  entity: ShareableEntity;
  menteeId: string;
  entityType: EntityType;
  activeTab?: "new" | "all";
  onNavigateToList?: () => void;
}

export function useEntityInteractionHandlers({
  entity,
  menteeId,
  entityType,
  activeTab,
  onNavigateToList,
}: UseEntityInteractionHandlersOptions) {
  const { user } = useAuth();
  const markRead = useMarkEntityRead();
  const markUnread = useMarkEntityUnread();
  const addInteraction = useAddInteraction(menteeId);
  const { data: interactions } = useEntityInteractions(entity.id);

  const isRead = entity.isRead ?? false;
  const isFromCurrentUser = entity.owner.id === user?.id;

  const { dispatchEvent } = usePostHogEvent(`mentor_hub_${entityType}_opened`, {
    onMount: true,
    params: {
      [`${entityType}Id`]: entity.id,
    },
  });

  const handleMarkAsRead = async () => {
    if (!isRead) {
      try {
        await markRead.mutateAsync({ id: entity.id });
        dispatchEvent(`mentor_hub_${entityType}_marked_read`);
        if (activeTab === "new" && onNavigateToList) {
          onNavigateToList();
        }
      } catch (error) {
        console.error("Failed to mark as read:", error);
      }
    }
  };

  const handleMarkUnread = async () => {
    try {
      await markUnread.mutateAsync({ id: entity.id });
      dispatchEvent(`mentor_hub_${entityType}_marked_unread`);
    } catch (error) {
      console.error("Failed to mark as unread:", error);
    }
  };

  const toggleReadStatus = async () => {
    if (isRead) {
      await handleMarkUnread();
    } else {
      await handleMarkAsRead();
    }
  };

  const handleReaction = async (
    emoji: ReactionEmoji,
    metadata?: Record<string, unknown>,
  ) => {
    try {
      await addInteraction.mutateAsync({
        entityId: entity.id,
        params: {
          type: "reaction",
          content: emoji,
          ...(metadata ? { metadata } : {}),
        },
      });
      dispatchEvent(`mentor_hub_${entityType}_reacted`, {
        emoji,
        ...(metadata ?? {}),
      });
    } catch (error) {
      console.error("Failed to send reaction:", error);
    }
  };

  const handleComment = async (
    text: string,
    metadata?: Record<string, unknown>,
  ) => {
    try {
      await addInteraction.mutateAsync({
        entityId: entity.id,
        params: {
          type: "comment",
          content: text,
          ...(metadata ? { metadata } : {}),
        },
      });
      dispatchEvent(`mentor_hub_${entityType}_comment_sent`, metadata ?? {});
    } catch (error) {
      console.error("Failed to send comment:", error);
    }
  };

  const comments = transformInteractionsToComments(interactions, user?.id);
  const reactions = transformInteractionsToReactions(interactions);

  return {
    user,
    isRead,
    isFromCurrentUser,
    interactions,
    comments,
    reactions,
    isInteractionPending: addInteraction.isPending,
    dispatchEvent,
    handleMarkAsRead,
    handleMarkUnread,
    toggleReadStatus,
    handleReaction,
    handleComment,
  };
}
